import { useState, useEffect } from "react";
import { Receipt, ChevronDown, ChevronUp, Truck, PackageCheck, Download, MapPin, Package, Euro, FileText, Clock, Loader2, Percent, Warehouse, Fuel } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { jobTotalCost, extrasCost, hasAnyExtra } from "@/utils/jobCost";
import { ExtraServicesBadges } from "@/components/ExtraServicesEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("mv_admin_token")}` } });

const statusLabels = { in_attesa: "Presa in Carico", ritirato: "Ritirato", in_transito: "In Transito", consegnato: "Consegnato", problema: "Problema", annullato: "Annullato" };
const statusColors = { in_attesa: "bg-yellow-100 text-yellow-700", ritirato: "bg-blue-100 text-blue-700", in_transito: "bg-sky-100 text-sky-700", consegnato: "bg-green-100 text-green-700", problema: "bg-red-100 text-red-700", annullato: "bg-zinc-100 text-zinc-500" };
const serviceLabels = { standard: "Standard", express: "Express", dedicato: "Dedicato", groupage: "Groupage" };

function totalColli(colli) {
  if (!colli || !colli.length) return 0;
  return colli.reduce((sum, c) => sum + (parseInt(c.quantita) || 1), 0);
}

function primaryName(d, fallbackCity) {
  if (!d) return fallbackCity || "-";
  return d.name || d.company || fallbackCity || "-";
}

function secondaryLines(d) {
  if (!d) return [];
  const locality = [d.cap, d.city, d.province].filter(Boolean).join(" ");
  return [d.address, locality, d.phone].filter(Boolean);
}

export default function AdminInvoicing() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [expanded, setExpanded] = useState(null);
  const [invoiceNumbers, setInvoiceNumbers] = useState({});
  const [savingNum, setSavingNum] = useState(null);
  const [discounts, setDiscounts] = useState({}); // { clientEmail: { global_discount: 0, item_discounts: { tracking: pct } } }

  useEffect(() => {
    axios.get(`${API}/admin/invoicing?month=${month}`, authHeaders())
      .then((r) => setData(r.data))
      .catch(() => {});
    axios.get(`${API}/admin/invoicing/numbers?month=${month}`, authHeaders())
      .then((r) => setInvoiceNumbers(r.data || {}))
      .catch(() => {});
    axios.get(`${API}/admin/invoicing/discounts?month=${month}`, authHeaders())
      .then((r) => setDiscounts(r.data || {}))
      .catch(() => {});
  }, [month]);

  const saveInvoiceNumber = (clientEmail, value) => {
    setInvoiceNumbers((prev) => ({ ...prev, [clientEmail]: value }));
    setSavingNum(clientEmail);
    axios.put(`${API}/admin/invoicing/numbers`, { month, client_email: clientEmail, invoice_number: value }, authHeaders())
      .then(() => setSavingNum(null))
      .catch(() => setSavingNum(null));
  };

  const fuel = data?.fuel_surcharge || { enabled: false, pct: 0 };
  const fuelApplies = (clientEmail) => {
    const o = discounts[clientEmail]?.fuel_surcharge_apply;
    return o === null || o === undefined ? !!fuel.enabled : !!o;
  };
  const fuelAmountFor = (c) => {
    if (!fuelApplies(c.email) || !(fuel.pct > 0)) return 0;
    const d = discounts[c.email] || { item_discounts: {} };
    const net = [...(c.shipments || []), ...(c.pickups || [])].reduce((s, x) => s + jobTotalCost(x) * (1 - (parseFloat(d.item_discounts?.[x.tracking_number] || 0) / 100)), 0);
    return net > 0 ? Math.round(net * fuel.pct) / 100 : 0;
  };
  const setFuelApply = (clientEmail, apply) => {
    const curr = discounts[clientEmail] || { global_discount: 0, item_discounts: {} };
    saveDiscounts(clientEmail, { ...curr, fuel_surcharge_apply: apply });
  };

  const saveDiscounts = (clientEmail, disc) => {
    setDiscounts((prev) => ({ ...prev, [clientEmail]: disc }));
    axios.put(`${API}/admin/invoicing/discounts`, { month, client_email: clientEmail, ...disc }, authHeaders()).catch(() => {});
  };

  const setItemDiscount = (clientEmail, tracking, pct) => {
    const curr = discounts[clientEmail] || { global_discount: 0, item_discounts: {} };
    const updated = { ...curr, item_discounts: { ...curr.item_discounts, [tracking]: parseFloat(pct) || 0 } };
    saveDiscounts(clientEmail, updated);
  };

  const setGlobalDiscount = (clientEmail, pct) => {
    const curr = discounts[clientEmail] || { global_discount: 0, item_discounts: {} };
    const updated = { ...curr, global_discount: parseFloat(pct) || 0 };
    saveDiscounts(clientEmail, updated);
  };

  const toggle = (email) => setExpanded(expanded === email ? null : email);

  const updateItemCost = async (itemType, itemId, newCosto, clientEmail) => {
    try {
      await axios.put(`${API}/admin/invoicing/update-cost`, { type: itemType, id: itemId, costo: newCosto }, authHeaders());
      // Reload data
      const res = await axios.get(`${API}/admin/invoicing?month=${month}`, authHeaders());
      setData(res.data);
    } catch {
      toast.error("Errore aggiornamento costo");
    }
  };

  const downloadPdf = async (clientEmail) => {
    try {
      const invoiceNum = clientEmail ? (invoiceNumbers[clientEmail] || "") : "";
      let url = clientEmail
        ? `${API}/admin/invoicing/pdf?month=${month}&client_email=${encodeURIComponent(clientEmail)}`
        : `${API}/admin/invoicing/pdf?month=${month}`;
      if (invoiceNum) url += `&invoice_number=${encodeURIComponent(invoiceNum)}`;
      const res = await axios.get(url, { ...authHeaders(), responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = clientEmail ? `fatturazione_${month}_${clientEmail}.pdf` : `fatturazione_${month}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("PDF scaricato!");
    } catch (err) {
      // Try to extract backend error message (when responseType=blob, the error body is also a Blob)
      let detail = "Errore nel download PDF";
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) detail = parsed.detail;
        } else if (err.response?.data?.detail) {
          detail = err.response.data.detail;
        }
      } catch (_) { /* ignore parse errors */ }
      console.error("[invoicing PDF]", err.response?.status, detail, err);
      toast.error(detail);
    }
  };

  return (
    <div data-testid="admin-invoicing">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-zinc-500">Riepilogo mensile per fatturazione</p>
        <div className="flex items-center gap-3">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none"
            data-testid="invoicing-month"
          />
          <button
            onClick={() => downloadPdf()}
            className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors"
            data-testid="download-all-pdf"
          >
            <Download size={16} /> PDF Totale
          </button>
        </div>
      </div>

      {!data ? (
        <div className="text-center py-12 text-zinc-400">Caricamento...</div>
      ) : data.clients.length === 0 ? (
        <div className="text-center py-12">
          <Receipt size={40} className="text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500">Nessuna attivita per {month}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.clients.map((c) => (
            <div key={c.email} className="bg-white border border-zinc-200 overflow-hidden" data-testid={`client-group-${c.client_code || c.email}`}>
              <div
                onClick={() => toggle(c.email)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-zinc-50 transition-colors cursor-pointer"
                data-testid={`expand-client-${c.client_code || c.email}`}
              >
                <div className="flex items-center gap-4">
                  <span className="px-2 py-1 bg-[#FF5200]/10 text-[#FF5200] text-xs font-bold font-mono rounded-sm whitespace-nowrap">
                    {c.client_code || "?"}
                  </span>
                  <div className="text-left">
                    <p className="font-bold text-[#09090B] font-heading">{c.client_name}</p>
                    <p className="text-xs text-zinc-500">{c.company ? `${c.company} | ` : ""}{c.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1.5 text-blue-600">
                      <Truck size={14} /> {c.total_shipments} sped.
                    </span>
                    <span className="flex items-center gap-1.5 text-purple-600">
                      <PackageCheck size={14} /> {c.total_pickups} ritiri
                    </span>
                    {(() => {
                      const wh = c.warehouse?.total || 0;
                      const fuelAmt = fuelAmountFor(c);
                      const imponibile = (c.shipments || []).reduce((s, x) => s + jobTotalCost(x), 0) + (c.pickups || []).reduce((s, x) => s + jobTotalCost(x), 0) + wh + fuelAmt;
                      const iva = imponibile * 0.22;
                      const totale = imponibile + iva;
                      return imponibile > 0 ? (
                        <>
                          {fuelAmt > 0 && (
                            <span className="flex items-center gap-1.5 text-amber-700" data-testid={`fuel-total-${c.client_code || c.email}`}>
                              <Fuel size={14} /> +{fuel.pct}% carb.
                            </span>
                          )}
                          {wh > 0 && (
                            <span className="flex items-center gap-1.5 text-violet-600" data-testid={`warehouse-total-${c.client_code || c.email}`}>
                              <Warehouse size={14} /> {wh.toFixed(2)} mag.
                            </span>
                          )}
                          <span className="flex items-center gap-1.5 font-bold text-[#FF5200]" data-testid={`row-total-${c.client_code || c.email}`}>
                            <Euro size={14} /> {totale.toFixed(2)} EUR <span className="text-[10px] font-normal text-zinc-400">(IVA 22% incl.)</span>
                          </span>
                        </>
                      ) : null;
                    })()}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Nr. Fattura (es. 001/2026)"
                      value={invoiceNumbers[c.email] || ""}
                      onChange={(e) => setInvoiceNumbers((prev) => ({ ...prev, [c.email]: e.target.value }))}
                      onBlur={(e) => saveInvoiceNumber(c.email, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-8 w-44 px-2 border border-zinc-200 rounded-sm text-xs focus:border-[#FF5200] outline-none"
                      data-testid={`invoice-number-${c.client_code || c.email}`}
                    />
                    {savingNum === c.email && <Loader2 size={12} className="animate-spin text-zinc-400" />}
                    <button
                      onClick={(e) => { e.stopPropagation(); downloadPdf(c.email); }}
                      className="p-1.5 text-[#FF5200] hover:bg-[#FF5200]/10 rounded-sm"
                      title="Scarica PDF"
                      data-testid={`download-pdf-${c.client_code || c.email}`}
                    >
                      <Download size={16} />
                    </button>
                  </div>
                  {expanded === c.email ? <ChevronUp size={18} className="text-zinc-400" /> : <ChevronDown size={18} className="text-zinc-400" />}
                </div>
              </div>

              {expanded === c.email && (
                <div className="border-t border-zinc-200 p-6">
                  {c.shipments.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-xs font-bold tracking-widest uppercase text-zinc-500 mb-3 font-heading flex items-center gap-2">
                        <Truck size={14} /> Spedizioni ({c.shipments.length})
                      </h4>
                      <div className="space-y-3">
                        {c.shipments.map((s) => {
                          const disc = discounts[c.email]?.item_discounts?.[s.tracking_number] || 0;
                          const costo = jobTotalCost(s);
                          const scontato = costo - (costo * disc / 100);
                          return (
                            <div key={s.id} className="flex items-center gap-3">
                              <div className="flex-1"><ShipmentCard item={s} type="spedizione" onCostUpdate={(type, id, costo) => updateItemCost(type, id, costo, c.email)} /></div>
                              {costo > 0 && (
                                <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                  <Percent size={12} className="text-zinc-400" />
                                  <input
                                    type="number" min="0" max="100" step="1"
                                    value={disc || ""}
                                    placeholder="0"
                                    onChange={(e) => setItemDiscount(c.email, s.tracking_number, e.target.value)}
                                    className="w-14 h-7 px-1.5 text-xs border border-zinc-200 rounded-sm text-center focus:border-[#FF5200] outline-none"
                                    data-testid={`item-discount-${s.tracking_number}`}
                                  />
                                  {disc > 0 && <span className="text-[10px] text-green-600 font-bold">{scontato.toFixed(2)}</span>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {c.pickups.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-xs font-bold tracking-widest uppercase text-zinc-500 mb-3 font-heading flex items-center gap-2">
                        <PackageCheck size={14} /> Ritiri ({c.pickups.length})
                      </h4>
                      <div className="space-y-3">
                        {c.pickups.map((p) => {
                          const disc = discounts[c.email]?.item_discounts?.[p.tracking_number] || 0;
                          const costo = jobTotalCost(p);
                          const scontato = costo - (costo * disc / 100);
                          return (
                            <div key={p.id} className="flex items-center gap-3">
                              <div className="flex-1"><ShipmentCard item={p} type="ritiro" onCostUpdate={(type, id, costo) => updateItemCost(type, id, costo, c.email)} /></div>
                              {costo > 0 && (
                                <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                  <Percent size={12} className="text-zinc-400" />
                                  <input
                                    type="number" min="0" max="100" step="1"
                                    value={disc || ""}
                                    placeholder="0"
                                    onChange={(e) => setItemDiscount(c.email, p.tracking_number, e.target.value)}
                                    className="w-14 h-7 px-1.5 text-xs border border-zinc-200 rounded-sm text-center focus:border-[#FF5200] outline-none"
                                    data-testid={`item-discount-${p.tracking_number}`}
                                  />
                                  {disc > 0 && <span className="text-[10px] text-green-600 font-bold">{scontato.toFixed(2)}</span>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {(c.warehouse?.total || 0) > 0 && <WarehouseSection wh={c.warehouse} code={c.client_code || c.email} />}

                  {/* Riepilogo con sconti */}
                  {(() => {
                    const clientDisc = discounts[c.email] || { global_discount: 0, item_discounts: {} };
                    let totLordo = 0;
                    let totScontoRighe = 0;
                    [...c.shipments, ...c.pickups].forEach((item) => {
                      const costo = jobTotalCost(item);
                      const pct = parseFloat(clientDisc.item_discounts?.[item.tracking_number] || 0);
                      totLordo += costo;
                      totScontoRighe += costo * pct / 100;
                    });
                    const whTot = c.warehouse?.total || 0;
                    totLordo += whTot;
                    const fuelAmt = fuelAmountFor(c);
                    const subDopo = totLordo - totScontoRighe + fuelAmt;
                    const glPct = parseFloat(clientDisc.global_discount || 0);
                    const scontoGl = subDopo * glPct / 100;
                    const imponibile = subDopo - scontoGl;
                    if (imponibile <= 0 && totLordo <= 0) return null;
                    const iva = imponibile * 0.22;
                    const totale = imponibile + iva;
                    return (
                      <div className="mt-4 border-t border-zinc-300 pt-4">
                        {/* Global Discount */}
                        <div className="flex items-center gap-3 mb-4" onClick={(e) => e.stopPropagation()}>
                          <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">Sconto globale %</label>
                          <input
                            type="number" min="0" max="100" step="1"
                            value={clientDisc.global_discount || ""}
                            placeholder="0"
                            onChange={(e) => setGlobalDiscount(c.email, e.target.value)}
                            className="w-16 h-8 px-2 text-sm border border-zinc-200 rounded-sm text-center focus:border-[#FF5200] outline-none"
                            data-testid={`global-discount-${c.client_code || c.email}`}
                          />
                          {glPct > 0 && <span className="text-xs text-green-600 font-bold">-{scontoGl.toFixed(2)} EUR</span>}
                        </div>
                        {fuel.pct > 0 && (
                          <label className="flex items-center gap-3 mb-4 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" checked={fuelApplies(c.email)} onChange={(e) => setFuelApply(c.email, e.target.checked)} className="w-4 h-4 accent-[#FF5200]" data-testid={`fuel-apply-${c.client_code || c.email}`} />
                            <span className="text-xs font-bold tracking-widest uppercase text-zinc-500 flex items-center gap-1.5"><Fuel size={12} className="text-amber-700" /> Supplemento carburante {fuel.pct}%</span>
                            <span className="text-[10px] text-zinc-400">(gasolio {Number(fuel.current_price).toFixed(3)} €/L · sul totale trasporti)</span>
                          </label>
                        )}
                        <div className="flex justify-end gap-8 text-sm">
                          <div className="text-right space-y-1">
                            {whTot > 0 && <p className="text-violet-600">di cui magazzino:</p>}
                            {fuelAmt > 0 && <p className="text-amber-700">Suppl. carburante {fuel.pct}%:</p>}
                            {glPct > 0 && <p className="text-green-600">Sconto:</p>}
                            <p className="text-zinc-500">Imponibile:</p>
                            <p className="text-zinc-500">IVA 22%:</p>
                            <p className="font-bold text-[#09090B] text-base">Totale:</p>
                          </div>
                          <div className="text-right space-y-1">
                            {whTot > 0 && <p className="text-violet-600 font-mono" data-testid={`summary-warehouse-${c.client_code || c.email}`}>{whTot.toFixed(2)} EUR</p>}
                            {fuelAmt > 0 && <p className="text-amber-700 font-mono" data-testid={`summary-fuel-${c.client_code || c.email}`}>+{fuelAmt.toFixed(2)} EUR</p>}
                            {glPct > 0 && <p className="text-green-600 font-mono">-{scontoGl.toFixed(2)} EUR</p>}
                            <p className="text-zinc-700 font-mono">{imponibile.toFixed(2)} EUR</p>
                            <p className="text-zinc-700 font-mono">{iva.toFixed(2)} EUR</p>
                            <p className="font-bold text-[#FF5200] text-base font-mono" data-testid={`summary-total-${c.client_code || c.email}`}>{totale.toFixed(2)} EUR</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function WarehouseSection({ wh, code }) {
  const d = wh.storage_detail || {};
  const rows = [
    ["Movimenti e preparazioni ordini", wh.movements],
    ["Stoccaggio bancali (pro-rata)", d.storage],
    ["Handling ingressi", d.ingressi],
    ["Handling uscite", d.uscite],
    ["Assicurazione All Risk (forfait)", d.insurance],
    ["Manodopera magazzino", wh.hours],
  ].filter(([, v]) => (v || 0) > 0);
  return (
    <div className="mb-6" data-testid={`warehouse-section-${code}`}>
      <h4 className="text-xs font-bold tracking-widest uppercase text-zinc-500 mb-3 font-heading flex items-center gap-2">
        <Warehouse size={14} /> Magazzino
      </h4>
      <div className="border border-violet-200 bg-violet-50/40 rounded-sm divide-y divide-violet-100">
        {rows.map(([label, v]) => (
          <div key={label} className="flex items-center justify-between px-4 py-2 text-sm">
            <span className="text-zinc-600">{label}</span>
            <span className="font-mono text-zinc-800">{v.toFixed(2)} EUR</span>
          </div>
        ))}
        <div className="flex items-center justify-between px-4 py-2 text-sm font-bold">
          <span className="text-violet-700">Totale magazzino</span>
          <span className="font-mono text-violet-700">{wh.total.toFixed(2)} EUR</span>
        </div>
      </div>
    </div>
  );
}


function ShipmentCard({ item, type, onCostUpdate }) {
  const s = item;
  const originLines = secondaryLines(s.origin_details);
  const destLines = secondaryLines(s.dest_details);
  const nColli = totalColli(s.colli);
  const [editingCost, setEditingCost] = useState(false);
  const [costValue, setCostValue] = useState(s.costo != null ? String(s.costo) : "");

  const saveCost = () => {
    const val = parseFloat(costValue) || 0;
    if (onCostUpdate) onCostUpdate(type === "spedizione" ? "shipment" : "pickup", s.id, val);
    setEditingCost(false);
  };

  return (
    <div className="border border-zinc-200 bg-zinc-50 rounded-sm overflow-hidden" data-testid={`invoice-item-${s.id}`}>
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-zinc-100">
        <div className="flex items-center gap-3">
          <span className="px-2 py-1 bg-[#FF5200]/10 text-[#FF5200] text-xs font-bold font-mono rounded-sm">{s.tracking_number}</span>
          <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest rounded-sm ${statusColors[s.status] || "bg-zinc-100 text-zinc-600"}`}>
            {statusLabels[s.status] || s.status}
          </span>
          {s.service_type && (
            <span className="px-2 py-1 bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase tracking-widest rounded-sm">
              {serviceLabels[s.service_type] || s.service_type}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm" onClick={(e) => e.stopPropagation()}>
          <span className="flex items-center gap-1 text-zinc-500"><Clock size={12} /> {s.created_at ? s.created_at.slice(0, 10).split("-").reverse().join("-") : ""}</span>
          {editingCost ? (
            <div className="flex items-center gap-1">
              <Euro size={12} className="text-[#FF5200]" />
              <input
                type="number" step="0.01" min="0"
                value={costValue}
                onChange={(e) => setCostValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveCost(); if (e.key === "Escape") setEditingCost(false); }}
                autoFocus
                className="w-20 h-7 px-1.5 text-xs font-mono border border-[#FF5200] rounded-sm text-right outline-none"
                data-testid={`cost-input-${s.id}`}
              />
              <button onClick={saveCost} className="text-green-600 text-xs font-bold hover:text-green-700" data-testid={`cost-save-${s.id}`}>OK</button>
            </div>
          ) : (
            <button
              onClick={() => { setCostValue(s.costo != null ? String(s.costo) : ""); setEditingCost(true); }}
              className="flex items-center gap-1 font-bold text-[#FF5200] hover:underline cursor-pointer"
              title="Clicca per modificare il costo base. Gli extra sono conteggiati a parte."
              data-testid={`cost-edit-${s.id}`}
            >
              <Euro size={12} />
              {(() => {
                const total = jobTotalCost(s);
                if (total <= 0) return <span className="text-zinc-400 font-normal text-xs">+ Aggiungi costo</span>;
                const ex = extrasCost(s);
                const base = parseFloat(s.costo) || 0;
                return ex > 0
                  ? <span>{total.toFixed(2)} EUR <span className="text-[10px] font-normal text-zinc-500">(base {base.toFixed(2)} + extra {ex.toFixed(2)})</span></span>
                  : <span>{total.toFixed(2)} EUR</span>;
              })()}
            </button>
          )}
        </div>
      </div>

      {hasAnyExtra(s) && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
          <ExtraServicesBadges value={s.extra_services} item={s} />
        </div>
      )}

      {/* Body */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-0 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
        {/* Origine */}
        <div className="p-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5 flex items-center gap-1"><MapPin size={10} /> Origine</p>
          <p className="text-sm font-bold text-[#09090B] uppercase">{primaryName(s.origin_details, s.origin)}</p>
          {originLines.map((l, i) => (
            <p key={`${s.id}-o-${i}`} className="text-xs text-zinc-500">{l}</p>
          ))}
        </div>

        {/* Destinazione */}
        <div className="p-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5 flex items-center gap-1"><MapPin size={10} /> Destinazione</p>
          <p className="text-sm font-bold text-[#09090B] uppercase">{primaryName(s.dest_details, s.destination)}</p>
          {destLines.map((l, i) => (
            <p key={`${s.id}-d-${i}`} className="text-xs text-zinc-500">{l}</p>
          ))}
        </div>

        {/* Colli */}
        <div className="p-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5 flex items-center gap-1"><Package size={10} /> Colli ({nColli})</p>
          {s.colli && s.colli.length > 0 ? s.colli.map((c, i) => (
            <div key={`${s.id}-c-${i}`} className="text-xs text-zinc-600 py-0.5">
              <span className="font-bold">{c.tipo === "pallet" ? "PLT" : "CLO"}{(parseInt(c.quantita) || 1) > 1 ? ` x${c.quantita}` : ""}</span>
              {c.peso && <span className="ml-1">{c.peso} kg</span>}
              {c.dimensioni && <span className="ml-1 text-zinc-400">| {c.dimensioni}</span>}
            </div>
          )) : <p className="text-xs text-zinc-400">-</p>}
        </div>

        {/* Extra info */}
        <div className="p-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1.5 flex items-center gap-1"><FileText size={10} /> Info</p>
          {s.ddt_reference && <p className="text-xs text-zinc-600"><span className="font-bold">DDT:</span> {s.ddt_reference}</p>}
          {s.note && <p className="text-xs text-zinc-600"><span className="font-bold">Note:</span> {s.note}</p>}
          {s.scheduled_date && <p className="text-xs text-zinc-600"><span className="font-bold">Data prevista:</span> {s.scheduled_date.split("-").reverse().join("-")}</p>}
          {s.goods_ready_status && <p className="text-xs text-zinc-600"><span className="font-bold">Merce:</span> {s.goods_ready_status === "pronta" ? "Pronta" : `Pronta alle ${s.goods_ready_time || ""}`}</p>}
          {!s.ddt_reference && !s.note && !s.scheduled_date && <p className="text-xs text-zinc-400">-</p>}
        </div>
      </div>
    </div>
  );
}
